import React, { Component, Fragment } from 'react'
import { reaction } from 'mobx'
import i18n from 'i18next'
import { observer } from 'mobx-react'
import AnimateHeight from 'react-animate-height'
import * as R from 'ramda'
import { Bar } from 'frontend-charts'
import chartRenderSettingsV3 from '../../../DynamicDashV3/chartRenderSettings'
import chartRenderSettings from '../../../DynamicDash/chartRenderSettings'
import MergeFields from '../../../../utils/MergeFields'
import Panel from '../../../../UniformComponents/Panel'
import PanelHeader from '../../../../UniformComponents/PanelHeader'
import Badge from '../../../../UniformComponents/Badge'

@observer
class ReportStat extends Component {
    constructor(props) {
        super(props)
        const { store } = props
        const { survey } = store
        this.isParent = R.pathOr(false, ['metadata', 'isParent'], survey)
        this.state = {
            updated: new Date().getTime(),
            isOpen: true,
            showTest: false,
        }

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                this.setState({
                    updated: props.reportStore.reportDataFetched,
                    showTest: props.reportStore.selectedResponseStatuses.has('test'),
                })
            },
        )
    }

    componentWillUnmount() {
        const { reportStore } = this.props
        reportStore.questionChartRefs.delete('reportStats')
    }

    chart = () => {
        const { data, store, reportStore } = this.props
        const { survey } = store
        if (this.isParent) return null
        const categories = survey.pages
            .filter((page) => page.pageType === 'ST')
            .map((page, i) => `${i + 1} ${MergeFields.replace(page.title, reportStore.locale, reportStore.mergeFields)}`)

        const chartData = [
            {
                name: i18n.t('report_dropoff_completions'),
                data: data.details.complete,
            },
            {
                name: i18n.t('report_dropoff_dropoffs'),
                data: data.details.drop,
            },
            {
                name: i18n.t('report_dropoff_screening_exits'),
                data: data.details.screen,
            },
        ]

        const dashStore = {
            appStore: reportStore.appStore,
            benchmarkPeriods: [
                {
                    value: 'ROL36',
                    label: '36 Months',
                    benchmarkType: 'ROL',
                    period: { periodType: 'ROL', duration: 36 },
                    ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 36 },
                },
                {
                    value: 'ROL84',
                    label: 'Long-term',
                    benchmarkType: 'ROL',
                    period: { periodType: 'ROL', duration: 84 },
                    ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 84 },
                },
            ],
            benchmarkPeriod: 'ROL36',
            oId: 6,
            getChartScope: () => 'event',
        }

        const crs = process.env.NEW_WIDGET_CREATOR ? chartRenderSettingsV3 : chartRenderSettings
        const layoutEdits = crs(
            {
                chartType: 'column',
                scope: 'event',
                widgetRenderSettings: {
                    showDataLabels: false,
                    showOptionLabels: false,
                    labelPosition: 'top',
                },
            },
            dashStore,
            categories,
            chartData,
        )

        const legendPadding = [
            {
                path: ['grid', 'bottom'],
                value: '24',
            },
            {
                path: ['legend', 'padding'],
                value: [
                    5, 5, 0, 5,
                ],
            }]

        const ReportStatChart = (
            <div style={{ padding: '24px' }}>
                <Bar
                    xAxisLabels={categories}
                    chartData={chartData}
                    style={{ width: '100%', height: '400px' }}
                    showLegend
                    tooltipDecimals={0}
                    layoutEdit={[...layoutEdits.layoutEdit, ...legendPadding]}
                    defaultSeries={layoutEdits.defaultSeries}
                    setRef={(c) => { reportStore.questionChartRefs.set('reportStats', c) }}
                />
            </div>
        )

        return ReportStatChart
    }

    generateDataTable = () => {
        const { reportStore } = this.props
        const { showTest } = this.state
        const { reportPages } = reportStore
        const data = reportStore.reportData.stats
        return (
            <Fragment>
                <div className="panel question">
                    <table
                        className="datatable tablesorter tablesorter-default"
                        style={{
                            width: '100%',
                        }}
                    >
                        <thead>
                            <tr className="col tablesorter-headerRow">
                                <th className="tleft tablesorter-header">
                                    <div className="tablesorter-header-inner">{i18n.t('report_table_colheader_title')}</div>
                                </th>
                                <th className="col completions tablesorter-header">
                                    <Badge
                                        type="success"
                                        cssClass="rounded center headerBadge"
                                        label={i18n.t('report_dropoff_completions')}
                                        icon="fa-regular fa-circle-check"
                                        style={{ backgroundColor: 'unset', color: 'inherit' }}
                                    />
                                </th>
                                <th className="col dropoffs tablesorter-header">
                                    <Badge
                                        type="warning"
                                        cssClass="rounded center headerBadge"
                                        label={i18n.t('report_dropoff_dropoffs')}
                                        icon="fa-regular fa-circle-arrow-down"
                                        style={{
                                            backgroundColor: 'unset', color: 'inherit',
                                        }}
                                    />
                                </th>
                                <th className="col screening tablesorter-header">
                                    <Badge
                                        type="error"
                                        cssClass="rounded center headerBadge"
                                        label={i18n.t('report_dropoff_screening_exits')}
                                        icon="fa-regular fa-ban"
                                        style={{ backgroundColor: 'unset', color: 'inherit' }}
                                    />
                                </th>
                                <th className="tlast">
                                    <div />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {!this.isParent ? reportPages.map((page, i) => (
                                <tr className="col" key={`stats_${page.id}`}>
                                    <th
                                        title={
                                            MergeFields.replace(
                                                page.title,
                                                reportStore.locale,
                                                reportStore.mergeFields,
                                            )
                                        }
                                    >
                                        <a
                                            className="scrollToQuestion"
                                            pageId={page.id}
                                            onClick={this.props.scrollTo}
                                        >
                                            {
                                                MergeFields.replace(
                                                    `${(i + 1)} ${page.title}`,
                                                    reportStore.locale,
                                                    reportStore.mergeFields,
                                                )
                                            }
                                        </a>
                                    </th>
                                    <td>
                                        <p>{R.pathOr(0, ['details', 'complete', i], data)}</p>
                                    </td>
                                    <td>
                                        <p>{R.pathOr(0, ['details', 'drop', i], data)}</p>
                                    </td>
                                    <td>
                                        <p>{R.pathOr(0, ['details', 'screen', i], data)}</p>
                                    </td>
                                    <td className="tlast" />
                                </tr>
                            )) : null}
                        </tbody>
                        <tfoot>
                            <tr className="col">
                                <th>
                                    {i18n.t('report_total_responses')}
                                    {showTest && (
                                        <div style={{ fontSize: 10, fontWeight: 700, fontStyle: 'italic' }}>
                                            {i18n.t('report_total_responses_valid')}
                                            <span
                                                style={{ fontWeight: 'normal', fontStyle: 'normal', color: 'rgb(139, 153, 167)', marginLeft: 5 }}
                                            >
                                                {i18n.t('report_total_responses_test')}
                                            </span>
                                        </div>
                                    )}
                                </th>
                                <td>
                                    <p>
                                        {R.pathOr(0, ['aggregate', 'total', 'complete'], data)}
                                        {showTest && ` (${R.pathOr(0, ['aggregate', 'test', 'complete'], data)})`}
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        {R.pathOr(0, ['aggregate', 'total', 'drop'], data)}
                                        {showTest && ` (${R.pathOr(0, ['aggregate', 'test', 'drop'], data)})`}
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        {R.pathOr(0, ['aggregate', 'total', 'screen'], data)}
                                        {showTest && ` (${R.pathOr(0, ['aggregate', 'test', 'screen'], data)})`}
                                    </p>
                                </td>
                                <td className="tlast" />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </Fragment>
        )
    }

    scrollTop = () => {
        window.scrollTo(0, 0)
    }

    panels = () => {
        const { reportStore } = this.props
        const { showTest } = this.state
        const data = reportStore.reportData.stats
        return (
            <div id="statPanels" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Panel cssClass="stats">
                    <div className="iconCircle">
                        <i className="fa-light fa-layer-group" />
                    </div>
                    <div className="details">
                        <label>Total responses</label>
                        <span>
                            {R.pathOr(0, ['aggregate', 'total', 'complete'], data)}
                            {showTest && ` (${R.pathOr(0, ['aggregate', 'test', 'complete'], data)})`}
                        </span>
                    </div>
                </Panel>
                {!this.isParent && (
                    <Panel cssClass="stats">
                        <div className="iconCircle">
                            <i className="fa-regular fa-envelope-circle-check" />
                        </div>
                        <div className="details">
                            <label>Email responses</label>
                            <span>
                                {R.pathOr(0, ['aggregate', 'emailComplete'], data).toFixed(0)}
                                %
                            </span>
                        </div>
                    </Panel>
                )}
                <Panel cssClass="stats">
                    <div className="iconCircle">
                        <i className="fa-light fa-door-open" />
                    </div>
                    <div className="details">
                        <label>Screening exits</label>
                        <span>
                            {R.pathOr(0, ['aggregate', 'total', 'screen'], data)}
                        </span>
                    </div>
                </Panel>
            </div>
        )
    }

    toggleOpen = () => {
        const { isOpen } = this.state
        this.setState({ isOpen: !isOpen })
    }

    render() {
        const { loading } = this.props
        const { isOpen } = this.state
        const chevron = isOpen ? 'up' : 'down'

        return (
            <Panel>
                <PanelHeader>
                    <div style={{
                        display: 'flex', padding: '15px', gap: '10px', alignItems: 'center',
                    }}
                    >
                        <i
                            className={`fa-thin fa-chevron-${chevron}`}
                            onClick={this.toggleOpen}
                            style={{
                                color: '#715AFF',
                                backgroundColor: '#F7F5FF',
                                borderRadius: '3px',
                                padding: '10px',
                                cursor: 'pointer',
                            }}
                        />
                        <h4
                            style={{
                                color: '#102E4A',
                                margin: '0px',
                            }}
                        >
                            {i18n.t('report_dropofftitle')}
                        </h4>
                    </div>
                </PanelHeader>
                <AnimateHeight
                    duration={250}
                    height={isOpen ? 'auto' : 0} // see props documentation below
                >
                    <div id="questionData_stats">
                        {this.panels()}
                        <div
                            className="browser"
                            id="question_surveyDropOff"
                            style={{
                                position: 'relative',
                                zIndex: 1,
                                visibility: 'visible',
                            }}
                        >
                            { !loading && this.generateDataTable() }
                        </div>
                    </div>
                    {this.chart()}
                </AnimateHeight>
            </Panel>
        )
    }
}

export default ReportStat
